import axios from "axios";
import { PAYMENT_CATEGORY } from "constants/constants";
import {
  Payhere,
  AccountCategory,
  Customer,
  CheckoutParams,
  CurrencyType,
  PayhereCheckout,
} from "payhere-js-sdk";

// --- Global Initialization ---

/**
 * Initializes the Payhere SDK with the correct merchant ID and environment.
 */
// export const initializePayhere = () => {
//   // Check if already initialized in a production app context if needed, but safe to call.
  
// if (process.env.REACT_APP_PAYHERE_ENV === "SANDBOX") {
//   Payhere.init(
//     process.env.REACT_APP_PAYHERE_SANDBOX_MERCHANT_ID,
//     AccountCategory.SANDBOX
//   );
// } else {
//   Payhere.init(
//     process.env.REACT_APP_PAYHERE_MERCHANT_ID,
//     AccountCategory.LIVE
//   );
// }
// }

export const initializePayhere = () => {
  const env = (process.env.REACT_APP_PAYHERE_ENV || "").trim().toUpperCase();
  const isSandbox = env === "SANDBOX";

  // console.log(
  //   `[PayHere Init] Environment: ${env} | Using ${
  //     isSandbox ? "SANDBOX" : "LIVE"
  //   } merchant credentials`
  // );

  if (isSandbox) {
    Payhere.init(
      process.env.REACT_APP_PAYHERE_SANDBOX_MERCHANT_ID,
      AccountCategory.SANDBOX
    );
  } else {
    Payhere.init(
      process.env.REACT_APP_PAYHERE_MERCHANT_ID,
      AccountCategory.LIVE
    );
  }
};
initializePayhere(); // Initialize upon file load

// =======================================================
// 1. PAYMENT RECORD CREATION (Backend API Call)
// =======================================================

/**
 * Creates the Payment record in the backend and receives the Payhere hash.
 *
 * @param {string} appointmentId - The ID of the newly created appointment.
 * @param {number} fee - The fee amount.
 * @param {string} userId - The ID of the user initiating payment.
 * @param {string} token - Authentication token.
 * @returns {Promise<{paymentRef: string, hash: string}>}
 */
export const createPaymentRecordService = async (
  appointmentId,
  fee,
  userId,
  token
) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    

// Detect sandbox mode (used for safety flag)
// const isSandbox = process.env.REACT_APP_PAYHERE_ENV === "SANDBOX";


    const paymentData = {
      orderRef: appointmentId,
      txnAmount: fee.toFixed(2),
      category: PAYMENT_CATEGORY.APPOINTMENT,
      userId: userId,
      payType: "FP",
      // paymentId: isSandbox ? "test_id" : undefined,
      // sandbox: isSandbox, // optional; backend can ignore if unused
    };

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}payment`,
      paymentData,
      config
    );

    if (response.status === 201 && response.data?.data?.paymentRef) {
      return {
        paymentRef: response.data.data.paymentRef,
        hash: response.data.data.hash,
      };
    }
    throw new Error("Payment record creation failed");
  } catch (error) {
    console.error("Create payment error:", error);
    throw new Error(
      error.response?.data?.message || "Payment record creation failed"
    );
  }
};

// =======================================================
// 2. PAYHERE CHECKOUT (SDK Integration)
// =======================================================

/**
 * Initiates the Payhere SDK Checkout process.
 *
 * @param {object} data - Checkout data.
 * @param {object} data.payerDetails - Details of the user/guest making the payment.
 * @param {number} data.fee - The amount to charge.
 * @param {string} data.paymentRef - The order ID from the backend payment record.
 * @param {string} data.hash - The security hash from the backend payment record.
 * @param {string} data.appointmentId - The final appointment ID for return URL.
 */
export const initiatePayhereCheckout = (data) => {
  const { payerDetails, fee, paymentRef, hash, appointmentId } = data;

  return new Promise((resolve, reject) => {
    try {
      const customer = new Customer({
        first_name: payerDetails.firstName || payerDetails.name,
        last_name: payerDetails.lastName,
        phone: payerDetails.phoneNumber,
        email: payerDetails.email,
        address: payerDetails.address || "N/A",
        city: "Colombo",
        country: "Sri Lanka",
      });

      // @ts-ignore
      const checkoutData = new CheckoutParams({
        returnUrl: `${process.env.REACT_APP_FRONTEND_BASE_URL}payment-forapt/thank-you/${appointmentId}`,
        cancelUrl: `${process.env.REACT_APP_FRONTEND_BASE_URL}payment/cancel`,
        notifyUrl: `${process.env.REACT_APP_BACKEND_URL}payment/confirmation`,
        order_id: paymentRef,
        itemTitle: PAYMENT_CATEGORY.APPOINTMENT,
        currency: CurrencyType.LKR,
        amount: Math.floor(fee),
        hash: hash,
      });

      const checkout = new PayhereCheckout(customer, checkoutData, (error) => {
        console.error("Payhere Checkout Error:", error);
        reject(new Error("Payment gateway error. Please try again."));
      });

      // Start checkout after a small delay
      setTimeout(() => {
        checkout.start();
        // Resolve immediately as the browser is redirecting away
        resolve({ success: true, message: "Redirecting to payment gateway." });
      }, 100);
    } catch (error) {
      console.error("Payment checkout error:", error);
      reject(new Error("Failed to initialize payment checkout."));
    }
  });
};
